<template>
  <div class="d-inline">
    <b-button
      @click="showModal"
      size="sm"
      :variant="isCompanyNameDefined ? 'outline-primary' : 'primary'"
      :class="isCompanyNameDefined ? '' : 'mr-2'"
      :disabled="isDisabledAdd"
    >
      <i v-if="!isCompanyNameDefined" class="fa fa-plus" aria-hidden="true"></i>
      {{ title }}
    </b-button>

    <b-modal
      size="xl"
      :id="modalId"
      :ref="modalId"
      @ok="saveRecord"
      @show="onResetModal"
      ok-title="Add"
    >
      <template slot="modal-header">
        <h5 class="modal-title">{{ title }}</h5>
      </template>
      <div>
        <form>
          <slot name="headerSlot"></slot>
          <b-row>
            <b-col cols="5" v-if="showFilters[ADD_RECORD_FILTERS.CUSTOMER]">
              <b-form-group :label="companyLabel" class="required">
                <vue-simple-suggest
                  class="suggest-input form-control"
                  :class="{ 'is-invalid': $v.filterValObj.companyId.$error }"
                  :styles="autoCompleteStyle"
                  :list="getListOfNames"
                  :max-suggestions="20"
                  :min-length="3"
                  :debounce="200"
                  :filter-by-query="true"
                  value-attribute="id"
                  display-attribute="name"
                  v-model="filterValObj.companyName"
                  :disabled="isCompanyNameDefined"
                  @select="onSuggestNameSelect"
                  @input="suggestNameChange"
                  @blur="onCompanyBlur"
                  @request-done="onRequestDone"
                >
                  <div
                    slot="misc-item-below"
                    v-if="isLoading"
                    class="misc-item"
                  >
                    <span>Loading {{ companyLabel }}...</span>
                  </div>
                </vue-simple-suggest>
                <div class="invalid-feedback">
                  <div v-if="!$v.filterValObj.companyId.required">
                    {{ companyLabel }} is required
                  </div>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="3" v-if="showFilters[ADD_RECORD_FILTERS.CONTACT]">
              <b-form-group label="Contact">
                <b-form-select v-model="filterValObj.contactId">
                  <option
                    v-for="(select, index) in sortSelect(contactsArr,'firstName')"
                    :key="index"
                    :value="select.id"
                    >{{ select.firstName + ' ' + select.lastName }}</option
                  >
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col
              cols="5"
              v-if="showFilters[ADD_RECORD_FILTERS.ALTERNATIVE_PART]"
            >
              <b-form-group label="Alternative part">
                <b-form-input
                  class="form-input"
                  v-model="filterValObj.searchQuery"
                  @input="getListOfNames"
                  placeholder="Search"
                  left
                ></b-form-input>
              </b-form-group>
            </b-col>
            <slot name="moreFilters"></slot>
          </b-row>
          <slot name="subHeader"></slot>
          <b-row>
            <b-col>
              <div class="grid-container">
                <br />
<!--                :skip="skip"-->
<!--                :take="take"-->
                <grid-component
                  isResizable
                  isReorderable
                  isSortable
                  :pageable="pageable"
                  :gridId="gridId"
                  :itemData="items"
                  :pageSizes="pageInfo.pageSize"
                  :totalItems="pageInfo.totalItems"
                  :sortBy="pageInfo.sortBy"
                  :sortOrder="pageInfo.sortOrder"
                  :skip="pageInfo.pageSize * (pageInfo.pageNumber - 1)"
                  :take="pageInfo.pageSize"
                  :fields="fields"
                  :navigationField="navigationField"
                  :hideCheckBoxAction="hideActionBtn"
                  :handleRowClick="onRowClick"
                  :sortMethod="sortMethod"
                  @getData="onGetData"
                  @checkedItem="checkedRowItemIds = $event"
                  disablesDelete
                />
              </div>
            </b-col>
          </b-row>
          <b-row v-if="showFilters[ADD_RECORD_FILTERS.CUSTOMER]">
            <b-col>
              <required-descriptor />
            </b-col>
          </b-row>
        </form>
      </div>
      <template slot="modal-footer" v-if="showLeftFooterBtn">
        <b-button
          variant="primary"
          class="mr-auto"
          @click="saveRecord(1)"
          :disabled="disabledLeftBtn"
        >
          Create New {{ footerTerm }}</b-button
        >
        <b-button @click="hideModal">Cancel</b-button>
        <b-button
          variant="primary"
          @click="saveRecord(0)"
          :disabled="disabledRightBtn"
          >Add to {{ footerTerm }}</b-button
        >
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import VueSimpleSuggest from 'vue-simple-suggest';
import GridSettings from '@/shared/components/GridSettings';
import { required } from 'vuelidate/lib/validators';
import { date } from '@/shared/filters';
import GridComponent from '@/shared/components/GridComponent';

import { AUTO_COMPLETE_STYLES, ADD_RECORD_FILTERS, GRID_PAGE_SIZES } from '@/shared/constants';
import {
  GRID_ID,
  MODAL_ID,
  SAVE_SETTINGS_DELAY,
  PAGE_SIZE,
} from '@/shared/constants';
import { prepareColumnsData } from '@/shared/helpers';

export default {
  components: {
    GridSettings,
    VueSimpleSuggest,
    GridComponent,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    modalId: {
      type: String,
      default: MODAL_ID.DEFAULT,
    },
    showFilters: {
      type: Object,
      default: () => {
        return {
          customer: false,
          contact: false,
          alternativePart: false,
        };
      },
    },
    fields: {
      type: Array,
      default: () => [],
    },
    companyId: {
      type: [Number, String],
      default: 0,
    },
    items: {
      type: Array,
      default: () => [],
    },
    onSuggestNameChange: {
      type: Function,
    },
    onSuggestNameSelect: {
      type: Function,
    },
    getListOfNames: {
      type: Function,
    },
    resetModal: {
      type: Function,
    },
    saveRecord: {
      type: Function,
    },
    suggestionList: {
      type: Array,
      default: () => []
    },
    contactsArr: {
      type: Array,
      default: () => [],
    },
    filterValObj: {
      type: Object,
      default: () => {},
    },
    pageInfo: {
      type: Object,
      default: () => {
        return {
          pageSize: PAGE_SIZE,
          totalItems: 0,
          pageNumber: 1
        };
      },
    },
    isDisabledAdd: {
      type: Boolean,
      default: false,
    },
    hideActionBtn: {
      type: Boolean,
      default: false,
    },
    showLeftFooterBtn: {
      type: Boolean,
      default: false,
    },
    footerTerm: {
      type: String,
      default: '',
    },
    disabledLeftBtn: {
      type: Boolean,
      default: true,
    },
    disabledRightBtn: {
      type: Boolean,
      default: true,
    },
    isVendor: {
      type: Boolean,
      default: false,
    },
    isCompanyNameDefined: {
      type: Boolean,
      default: false,
    },
    navigationField: {
      type: String,
      default: 'id',
    },
    gridId: {
      type: String,
      default: null,
    },
    sortMethod: {
      type: Function
    },
    getDataMethod: {
      type: Function
    }
  },
  data() {
    return {
      ADD_RECORD_FILTERS,
      prepareColumnsData,
      autoCompleteStyle: AUTO_COMPLETE_STYLES,
      pageable: {
        pageSizes: GRID_PAGE_SIZES,
      },
      checkedRowItemIds: [],
      pageNumber: 1,
      skip: 0,
      take: 10,
      isLoading: false,
    };
  },
  validations: {
    filterValObj: {
      companyId: { required },
    },
  },
  filters: {
    date,
  },
  computed: {
    ...mapGetters('account', ['gridSettingsById']),
    gridSettings() {
      return this.gridSettingsById(this.gridId);
    },
    disableAdd() {
      return this.filterValObj.companyId == 0;
    },
    checkAllRows: {
      get() {
        //return false;
        return this.items && this.items.length > 0
          ? this.checkedRowItemIds.length === this.items.length
          : false;
      },
      set(value) {
        const checked = [];

        if (value) {
          this.items.forEach(item => {
            checked.push(item.id);
          });
        }

        this.checkedRowItemIds = checked;
      },
    },
    companyLabel() {
      return !this.isVendor ? 'Customers' : 'Vendors';
    },
  },
  methods: {
    onGetData(params) {
      if (this.getDataMethod != undefined)
        this.getDataMethod(params);
      else if (this.getListOfNames != undefined)
        this.getListOfNames(params);
      // this.skip = params.pageSize * (params.pageNumber - 1);
      // this.take = params.pageSize;
    },
    onCompanyBlur() {
      this.$v.filterValObj.companyId.$touch();
      if(this.filterValObj.companyName) {
        if (this.suggestionList.length > 0) {
          const findIndex = this.suggestionList.findIndex(x => this.filterValObj.companyName.toLowerCase() == x.name.toLowerCase());

          if (findIndex > -1) {
            this.onSuggestNameSelect(this.suggestionList[findIndex]);
          }
        }
      }
    },
    onResetModal() {
      this.checkedRowItemIds = [];
      this.resetModal();
    },
    showModal() {
      this.$emit('addItemClick');
      this.$bvModal.show(this.modalId);
    },
    hideModal() {
      this.$bvModal.hide(this.modalId);
    },
    // handlePageChange(event) {
    //   this.skip = event.page.skip;
    //   this.take = event.page.take;
    //   this.pageNumber = Math.floor(event.page.skip / event.page.take) + 1;
    //
    //   this.getListOfNames({
    //     ...this.gridSettings.sort,
    //     pageSize: this.take,
    //     pageNumber: this.pageNumber,
    //   });
    // },
    handleColumnResize: debounce(function({ columns }) {
      const newColumns = prepareColumnsData({
        columns,
        initialColumns: this.fields,
        settingsColumns: this.gridSettings.columns,
      });

      this.saveGridSettings({
        id: this.gridId,
        settings: { columns: newColumns },
      });
    }, SAVE_SETTINGS_DELAY),
    handleColumnReorder({ columns }) {
      const newColumns = prepareColumnsData({
        columns,
        initialColumns: this.fields,
        settingsColumns: this.gridSettings.columns,
      });

      this.saveGridSettings({
        id: this.gridId,
        settings: { columns: newColumns },
      });
    },
    onRowClick(data) {
      this.$emit('selectedInvoice', data.dataItem.id);
    },
    suggestNameChange() {
      this.isLoading = true;
      this.onSuggestNameChange();
    },
    onRequestDone() {
      this.isLoading = false;
    },
  },
  watch: {
    checkedRowItemIds() {
      this.$emit('updateCheckedRowItemIds', this.checkedRowItemIds);
    },
  },
};
</script>
